import { useState, useRef, useEffect } from "react";
import { Info } from "lucide-react";
import { formatShort, formatFull } from "@/lib/format";

// Displays a monetary amount (or count) with the uniform rule.
// When shortened (>= 10 000), shows a discrete icon; click reveals the full value.
export function Amount({ value, money = true, className = "", testid }) {
  const n = Number(value || 0);
  const short = formatShort(n);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    window.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDoc); window.removeEventListener("keydown", onKey); };
  }, [open]);

  if (!short) {
    return <span className={className} data-testid={testid}>{formatFull(n, money)}</span>;
  }

  const sign = n < 0 ? "-" : "";
  const disp = `${sign}${money ? "$" : ""}${short.replace("-", "")}`;
  const full = formatFull(n, money);

  return (
    <span ref={ref} className={`relative inline-flex items-center gap-0.5 ${className}`} data-testid={testid}>
      {disp}
      <button
        type="button"
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen((o) => !o); }}
        data-testid={testid ? `${testid}-detail` : undefined}
        aria-label="Voir le détail"
        className="inline-flex shrink-0 opacity-50 transition-opacity hover:opacity-100"
      >
        <Info className="h-3 w-3" />
      </button>
      {open && (
        <span
          role="tooltip"
          data-testid={testid ? `${testid}-full` : undefined}
          className="absolute bottom-full left-1/2 z-50 mb-1 -translate-x-1/2 whitespace-nowrap rounded-md border border-slate-700 bg-slate-900 px-2 py-1 text-xs font-normal text-white shadow-lg"
        >
          {full}
        </span>
      )}
    </span>
  );
}
